import React from 'react';
import { Formik } from 'formik';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { PaymentsConfiguration } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/types';
import { isOrganizationAuOrg } from '~/Utils';

// `satisfies keyof PaymentsConfiguration` validates the key against the real response contract at
// compile time — if the field is ever renamed, tsc fails here instead of silently lying (which a
// hand-written `as 'display_net_amounts'` cast would not). The literal type still flows through, so
// the Formik id, initialValues key and IncludeVatValues can never desync.
const DISPLAY_NET_AMOUNTS_FIELD_ID = 'display_net_amounts' satisfies keyof PaymentsConfiguration;
const REDUCE_RESERVES_BY_NET_PAID_FIELD_ID = 'reduce_reserves_by_net_paid' satisfies keyof PaymentsConfiguration;

type IncludeVatValues = Record<
  typeof DISPLAY_NET_AMOUNTS_FIELD_ID | typeof REDUCE_RESERVES_BY_NET_PAID_FIELD_ID,
  boolean
>;

interface IncludeVatConfigurationDialogProps {
  paymentsConfiguration: PaymentsConfiguration;
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (values: IncludeVatValues) => Promise<void>;
}

const IncludeVatConfigurationDialog: React.FC<IncludeVatConfigurationDialogProps> = ({
  paymentsConfiguration,
  disabled = false,
  onCancel,
  onSubmit,
}) => {
  const { classes } = useStyles();
  // the label follows the organization being configured, not the logged-in user's org, so it is
  // derived from the sysconfig context exactly as the parent tab derives it
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const vatLabel = isOrganizationAuOrg(organization) ? 'GST' : 'VAT';

  return (
    <Formik<IncludeVatValues>
      initialValues={{
        [DISPLAY_NET_AMOUNTS_FIELD_ID]: !!paymentsConfiguration?.[DISPLAY_NET_AMOUNTS_FIELD_ID],
        [REDUCE_RESERVES_BY_NET_PAID_FIELD_ID]: !!paymentsConfiguration?.[REDUCE_RESERVES_BY_NET_PAID_FIELD_ID],
      }}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          title={`Include ${vatLabel} in payment and recovery screens`}
          isDialog
          onClose={onCancel}
          preventClose={disabled || isSubmitting}
          maxWidth="xs"
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                {`When enabled, the finance screens display net amounts (excluding ${vatLabel}) instead of gross ones.`}
              </Text>
              <SwitchFormik id={DISPLAY_NET_AMOUNTS_FIELD_ID} label="Display net amounts" />
            </Grid>
            <Grid item xs={12}>
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                {`When enabled, approving a payment reduces the reserve by the net amount (excluding ${vatLabel}) rather than the gross amount, and cancelling it adds the same net amount back.`}
              </Text>
              <SwitchFormik id={REDUCE_RESERVES_BY_NET_PAID_FIELD_ID} label="Reduce reserves by Net paid" />
            </Grid>
          </Grid>
          <div className={classes.buttonsContainer}>
            <CancelButton disabled={disabled || isSubmitting} onClick={onCancel} />
            <Button
              variant="contained"
              color="primary"
              disabled={disabled || isSubmitting}
              onClick={() => handleSubmit()}
            >
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default IncludeVatConfigurationDialog;
